// ── Anonymous → signed-in handoff ─────────────────────────────────────────────
// After a guest signs up / logs in, folders the conversations they created as
// an anonymous visitor onto their account via `POST /v1/conversations/claim`,
// then clears the per-tab guest pickup id so the auth-owned list takes over.

import { claimAnonymousConversations } from "@/lib/api/chat.api"
import { getStoredAnonymousFingerprint } from "./fingerprint"
import { useCopilotStore } from "@/store/copilot.store"
import type { QueryClient } from "@tanstack/react-query"

export const CHAT_CONVERSATIONS_QUERY_KEY = ["chat", "conversations"]

/**
 * Claim an earlier guest session's conversations onto the just-signed-in user.
 *
 * Best-effort: failures are swallowed so a claim hiccup never blocks the auth
 * transition. Call this from the first post-auth frame, passing the fingerprint
 * captured from the guest session (see `getStoredAnonymousFingerprint`).
 */
export async function claimGuestConversations(queryClient?: QueryClient): Promise<void> {
  const anonymousId = getStoredAnonymousFingerprint()
  if (!anonymousId) return

  try {
    const { claimed } = await claimAnonymousConversations(anonymousId)
    // Guest pickup id is meaningless now — the auth-owned list is the source of
    // truth. Remove it even when nothing was claimed so a stale guest id can't
    // leak across identities.
    useCopilotStore.getState().clearConversation()
    if (claimed > 0) {
      void queryClient?.invalidateQueries({ queryKey: CHAT_CONVERSATIONS_QUERY_KEY })
    }
  } catch {
    // Best-effort — the user is signed in regardless.
  }
}